import type { InspectionTemplate } from 'inspection-types';

import type { ReorderQuestionsChange, ReorderSectionsChange } from './changes';
import { sortItems, sortSections } from './templateProjection';

export type ReorderMove = {
  activeId: string;
  overId: string;
};

export type QuestionReorderMove = ReorderMove & {
  sectionId: string;
};

export function moveById<T extends { id: string }>(
  list: readonly T[],
  activeId: string,
  overId: string
): T[] | null {
  const fromIndex = list.findIndex(entry => entry.id === activeId);
  const toIndex = list.findIndex(entry => entry.id === overId);

  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return null;
  }

  const moved = list.slice();
  const [entry] = moved.splice(fromIndex, 1);

  moved.splice(toIndex, 0, entry);

  return moved;
}

// Pass the template with pending reorders applied: a second drag starts from what the
// user sees, not from the last saved order
export function buildReorderSections(
  template: InspectionTemplate,
  { activeId, overId }: ReorderMove
): ReorderSectionsChange | null {
  const moved = moveById(sortSections(template.sections), activeId, overId);

  if (!moved) {
    return null;
  }

  return { kind: 'reorderSections', sectionIds: moved.map(({ id }) => id) };
}

export function buildReorderQuestions(
  template: InspectionTemplate,
  { sectionId, activeId, overId }: QuestionReorderMove
): ReorderQuestionsChange | null {
  const section = template.sections.find(({ id }) => id === sectionId);

  if (!section) {
    return null;
  }

  const moved = moveById(sortItems(section.items), activeId, overId);

  if (!moved) {
    return null;
  }

  return {
    kind: 'reorderQuestions',
    sectionId,
    itemIds: moved.map(({ id }) => id),
  };
}
